'use client'

import * as React from 'react'
import {
  IconKeyboardArrowDown,
  IconKeyboardArrowUp,
  IconTune,
  IconClose,
} from '@repo/design-system-cf/components/icons'
import { tw } from 'utils/classnames'
import * as Popover from 'primitives/popover'
import * as Dialog from 'primitives/dialog'
import { useBreakpoint } from '@repo/web-ui/hooks/use-breakpoints'
import type { FeedSortingOption } from '@repo/web-ui/ask-cf/contexts/feed-sorting-context'
import { useFeedSorting } from '@repo/web-ui/ask-cf/contexts/feed-sorting-context'
import { Button } from '@repo/design-system-cf/components/button'
import { Z_INDEX_MODALS } from 'constants/modals'

export const ChooseFeedButton = () => {
  const { activeSortOption, setActiveSortOption } = useFeedSorting()
  const { breakpoint } = useBreakpoint()
  const isMobile = ['xs', 'sm'].includes(breakpoint ?? '')
  const [isOpen, setIsOpen] = React.useState(false)

  const feedOptions = [
    { value: 'discovery-feed', label: 'Discover' },
    { value: 'your-feed', label: 'People you follow' },
  ]

  const handleSortOption = (value: string) => {
    setActiveSortOption(value as FeedSortingOption)
    setIsOpen(false)
  }

  const buttonClassName = tw(
    'rounded-full flex gap-0.5 w-full items-center whitespace-nowrap sm:w-auto justify-center bg-cf-white border border-cf-purple-200 py-1.5 px-4 hover:bg-cf-purple-100 focus',
    'active:bg-cf-indigo-50 active:border-cf-purple-100',
  )

  const buttonContent = (
    <>
      <IconTune className="size-5 mr-1" />
      <p>{feedOptions.find(option => option.value === activeSortOption)?.label}</p>
      {isOpen ? (
        <IconKeyboardArrowUp className="size-5" />
      ) : (
        <IconKeyboardArrowDown className="size-5" />
      )}
    </>
  )

  if (isMobile) {
    return (
      <>
        <button className={buttonClassName} onClick={() => setIsOpen(true)}>
          {buttonContent}
        </button>
        <ChooseFeedButtonMobile
          isOpen={isOpen}
          closeModal={() => setIsOpen(false)}
          feedOptions={feedOptions}
          activeSortOption={activeSortOption}
          handleSortOption={handleSortOption}
        />
      </>
    )
  }

  return (
    <Popover.Root open={isOpen} onOpenChange={setIsOpen}>
      <Popover.Trigger asChild>
        <button className={buttonClassName}>{buttonContent}</button>
      </Popover.Trigger>
      <Popover.Content
        side="bottom"
        align="end"
        className="bg-cf-white p-1 box-shadow  min-w-60 rounded-2 max-w-96 shadow-[0px_2px_6px_2px_rgba(0,0,0,0.125)]"
        sideOffset={8}
      >
        {feedOptions.map(option => (
          <div
            className="flex flex-row items-center cursor-pointer justify-start p-2 gap-2 hover:bg-cf-grey-100 rounded relative w-full"
            onClick={() => handleSortOption(option.value)}
            key={option.value}
          >
            <p className="text-14 font-medium">{option.label}</p>
          </div>
        ))}
      </Popover.Content>
    </Popover.Root>
  )
}

function ChooseFeedButtonMobile({
  isOpen,
  closeModal,
  feedOptions,
  activeSortOption,
  handleSortOption,
}: {
  isOpen: boolean
  closeModal: () => void
  feedOptions: Array<{ value: string; label: string }>
  activeSortOption: string
  handleSortOption: (value: string) => void
}) {
  return (
    <Dialog.Root open={isOpen} onOpenChange={open => !open && closeModal()}>
      <React.Suspense fallback={null}>
        <Dialog.Portal>
          <Dialog.Body
            placement={'bottom'}
            className={tw(
              `font-poppins xl:items-center transition-transform duration-300 ease-in-out`,
              isOpen ? 'translate-y-0' : 'translate-y-full',
            )}
            overlayZIndex={Z_INDEX_MODALS.authentication.react - 1}
            zIndex={Z_INDEX_MODALS.authentication.react}
          >
            <div className="w-full md:max-w-xl rounded-t-[20px] bg-cf-white md:rounded-[12px] py-5 px-3 md:p-8 relative shadow-md">
              <div className="flex justify-between items-center pt-1 pb-4">
                <p className="text-16 font-semibold">Choose Feed</p>
                <Button
                  onClick={() => closeModal()}
                  className="pointer-events-auto"
                  variant="ghost"
                  iconLeft={<IconClose className="!size-6" />}
                />
              </div>
              <div className="flex flex-col gap-1 md:gap-3">
                {feedOptions.map(option => (
                  <div
                    key={option.value}
                    onClick={() => handleSortOption(option.value)}
                    className={tw(
                      'flex flex-row items-center cursor-pointer justify-start py-2.5 px-2 gap-2 hover:bg-cf-grey-100 rounded relative w-full',
                      activeSortOption === option.value && 'bg-cf-purple-50',
                    )}
                  >
                    <p className="text-14 font-medium">{option.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </Dialog.Body>
        </Dialog.Portal>
      </React.Suspense>
    </Dialog.Root>
  )
}
